import { useReducer, useCallback, useEffect, useRef, useState } from 'react'
import { apiClient, handleApiRequest } from '@/services/api/client'
import { extractErrorMessage } from '@/utils/errorHandler'

type TimeoutRef = ReturnType<typeof setTimeout> | null

interface DetectedMappingEntry {
  column: string
  confidence: number
  split_mode: string | null
}

interface PreviewRow {
  row_index: number
  first_name: string
  last_name: string
  email: string | null
  phone: string | null
  mobile: string | null
  teacher_code: string | null
  is_generated_teacher_code: boolean
  is_numbered_teacher_code: boolean
  room: string | null
  department: string | null
  subjects: string[] | null
  backgrounds: string[] | null
  subject_mapping_errors: string[] | null
  background_mapping_errors: string[] | null
  exists: boolean
  existing_id: string | null
  existing_role: string | null
  effective_role: string | null
  validation_errors: string[]
}

interface PreviewResult {
  success: boolean
  file_columns: string[]
  raw_rows: Record<string, string>[]
  sheets: string[]
  detected_mapping: Record<string, DetectedMappingEntry>
  preview_rows: PreviewRow[]
  total_rows: number
  warnings: string[]
  stats: { to_create: number; to_update: number; error_count: number }
  combined_info: string[] | null
  unmatched_subject_tokens: string[]
  unmatched_background_tokens: string[]
  available_subjects: string[]
  available_backgrounds: string[]
  needs_token_mapping: boolean
  needs_teacher_code_generation: boolean
  teacher_code_generation_method: string | null
  teacher_code_stats: {
    total_teacher_rows: number
    rows_with_file_code: number
    rows_with_generated_code: number
    rows_with_conflicts: number
    rows_without_code: number
  }
  teacher_code_conflicts: Array<{ row_index: number; code: string; reason: string }>
}

export interface ImportExecuteResult {
  success: boolean
  session_id: string
  created: number
  updated: number
  skipped: number
  errored: number
  errors: Array<Record<string, unknown>>
  warnings: string[]
}

interface ColumnMapping {
  fileColumn: string
  systemField: string | null
  confidence: number
  isAutoDetected: boolean
  split_mode: string | null
}

interface RoleEntry {
  role: string
  label: string
}

interface EmailGenerationConfig {
  pattern: string
  domain: string
  charMappings: Record<string, string>
  lowercase: boolean
}

interface TeacherCodeGenerationConfig {
  method: 'rule' | 'pattern'
  ruleType: string | null
  patternSyntax: string | null
}

interface TeacherCodeConflict {
  row_index: number
  code: string
  reason: string
}

interface TeacherCodeStats {
  total_teacher_rows: number
  rows_with_file_code: number
  rows_with_generated_code: number
  rows_with_conflicts: number
  rows_without_code: number
}

interface TeacherCodeSuggestions {
  activePattern: { id: string; name: string; syntax: string } | null
  autoInferredPattern: { pattern_syntax: string; confidence: number; matched_count: number; total_count: number; low_confidence?: boolean } | null
  availableRules: Array<{ type: string; description: string }>
  recommendation: 'pattern' | 'rule'
  withCode: number
  withoutCode: number
}

type ImportState = {
  step: 'upload' | 'editor' | 'result'
  uploadedFileId: string | null
  fileName: string
  targetRole: string
  fileColumns: string[]
  rawRows: Record<string, string>[]
  sheets: string[]
  selectedSheet: string
  totalRows: number
  totalRowsActual: number
  columnMapping: ColumnMapping[]
  splitMode: 'last_first' | 'first_last' | null
  previewRows: PreviewRow[]
  previewStats: { toCreate: number; toUpdate: number; errorCount: number } | null
  warnings: string[]
  skipRowIndices: Set<number>
  editedCells: Map<string, string>
  roleOverrides: Map<number, string>
  updateExisting: boolean
  skipDuplicates: boolean
  executing: boolean
  execResult: ImportExecuteResult | null
  loading: boolean
  needsPreview: boolean
  error: string | null
  uploading: boolean
  showRawData: boolean
  previewPage: number
  roles: RoleEntry[]
  combinedInfo: string[] | null
  unmatchedSubjectTokens: string[]
  unmatchedBackgroundTokens: string[]
  availableSubjects: string[]
  availableBackgrounds: string[]
  needsTokenMapping: boolean
  subjectTokenMapping: Record<string, string[]>
  backgroundTokenMapping: Record<string, string[]>
  showTokenMappingModal: boolean
  emailGenerationConfig: EmailGenerationConfig | null
  showEmailGenerationModal: boolean
  detectedSpecialChars: string[]
  teacherCodeGenerationConfig: TeacherCodeGenerationConfig | null
  showTeacherCodeGenerationModal: boolean
  teacherCodeSuggestions: TeacherCodeSuggestions | null
  teacherCodeStats: TeacherCodeStats | null
  teacherCodeConflicts: TeacherCodeConflict[]
  needsTeacherCodeGeneration: boolean
  teacherCodeGenerationMethod: string | null
  overwriteExistingCodes: boolean
  teacherCodeModalDismissed: boolean
  previewFailed: boolean
  previewRetryNonce: number
}

type ImportAction =
  | { type: 'SET_ROLES'; payload: RoleEntry[] }
  | { type: 'SET_TARGET_ROLE'; payload: string }
  | { type: 'UPLOAD_START' }
  | { type: 'UPLOAD_SUCCESS'; payload: { fileId: string; fileName: string } }
  | { type: 'PARSE_SUCCESS'; payload: PreviewResult }
  | { type: 'PREVIEW_SUCCESS'; payload: PreviewResult }
  | { type: 'UPDATE_MAPPING'; payload: { fileColumn: string; systemField: string | null } }
  | { type: 'UPDATE_SPLIT_MODE'; payload: 'last_first' | 'first_last' | null }
  | { type: 'SELECT_SHEET'; payload: string }
  | { type: 'SKIP_ROW'; payload: number }
  | { type: 'UNSKIP_ROW'; payload: number }
  | { type: 'EDIT_CELL'; payload: { rowIndex: number; field: string; value: string } }
  | { type: 'SET_UPDATE_EXISTING'; payload: boolean }
  | { type: 'SET_SKIP_DUPLICATES'; payload: boolean }
  | { type: 'EXECUTE_START' }
  | { type: 'EXECUTE_SUCCESS'; payload: ImportExecuteResult }
  | { type: 'SET_ERROR'; payload: string | null; previewFailed?: boolean }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_PREVIEW_PAGE'; payload: number }
  | { type: 'RESET' }
  | { type: 'GO_TO_STEP'; payload: 'upload' | 'editor' | 'result' }
  | { type: 'SET_SUBJECT_TOKEN_MAPPING'; payload: Record<string, string[]> }
  | { type: 'SET_BACKGROUND_TOKEN_MAPPING'; payload: Record<string, string[]> }
  | { type: 'SHOW_TOKEN_MAPPING_MODAL'; payload: boolean }
  | { type: 'SET_EMAIL_GENERATION_CONFIG'; payload: EmailGenerationConfig }
  | { type: 'SHOW_EMAIL_GENERATION_MODAL'; payload: boolean }
  | { type: 'SET_DETECTED_SPECIAL_CHARS'; payload: string[] }
  | { type: 'SET_ROLE_OVERRIDE'; payload: { rowIndex: number; role: string } }
  | { type: 'CLEAR_ROLE_OVERRIDE'; payload: number }
  | { type: 'SET_TEACHER_CODE_GENERATION_CONFIG'; payload: TeacherCodeGenerationConfig | null }
  | { type: 'SHOW_TEACHER_CODE_GENERATION_MODAL'; payload: boolean }
  | { type: 'SET_TEACHER_CODE_SUGGESTIONS'; payload: TeacherCodeSuggestions | null }
  | { type: 'SET_TEACHER_CODE_STATS'; payload: TeacherCodeStats | null }
  | { type: 'SET_TEACHER_CODE_CONFLICTS'; payload: TeacherCodeConflict[] }
  | { type: 'SET_OVERWRITE_EXISTING_CODES'; payload: boolean }
  | { type: 'DISMISS_TEACHER_CODE_GENERATION_MODAL' }
  | { type: 'RETRY_PREVIEW' }

const CELL_KEY_SEPARATOR = '::'

const FIELD_LABELS: Record<string, string> = {
  first_name: 'Vorname',
  last_name: 'Nachname',
  email: 'E-Mail',
  phone: 'Telefon',
  mobile: 'Mobil',
  teacher_code: 'Kürzel',
  room: 'Raum',
  department: 'Abteilung',
  subjects: 'Fächer',
  backgrounds: 'Hintergrund',
}

const REQUIRED_FIELDS: Record<string, string[]> = {
  teacher: ['first_name', 'last_name', 'email'],
  hr: ['first_name', 'last_name', 'email'],
  admin: ['first_name', 'last_name', 'email'],
}

const AVAILABLE_FIELDS: Record<string, string[]> = {
  teacher: ['first_name', 'last_name', 'email', 'phone', 'mobile', 'teacher_code', 'room', 'department', 'subjects', 'backgrounds'],
  hr: ['first_name', 'last_name', 'email', 'phone', 'mobile', 'room', 'department'],
  admin: ['first_name', 'last_name', 'email', 'phone', 'mobile'],
}

const SPECIAL_CHAR_DEFAULTS: Record<string, string> = {
  'ä': 'ae',
  'ö': 'oe',
  'ü': 'ue',
  'é': 'e',
  'è': 'e',
  'à': 'a',
  'â': 'a',
  'ô': 'o',
  'î': 'i',
  'ç': 'c',
  'ß': 'ss',
  'ñ': 'n',
  'æ': 'ae',
  'œ': 'oe',
}

const EMAIL_PATTERN_OPTIONS = [
  { value: '{first_name}.{last_name}', label: 'vorname.nachname (z.B. max.mustermann)' },
  { value: '{last_name}.{first_name}', label: 'nachname.vorname (z.B. mustermann.max)' },
  { value: '{first_name}_{last_name}', label: 'vorname_nachname (z.B. max_mustermann)' },
  { value: '{first_name}{last_name}', label: 'vorname+nachname (z.B. maxmustermann)' },
  { value: '{first_name[0]}.{last_name}', label: 'v.nachname (z.B. m.mustermann)' },
  { value: '{last_name}.{first_name[0]}', label: 'nachname.v (z.B. mustermann.m)' },
  { value: 'custom', label: 'Eigene Logik...' },
]

const PLACEHOLDER_HELP = `Verfügbare Platzhalter:
{first_name} → Vorname
{last_name} → Nachname
{first_name[0]} → Erster Buchstabe Vorname
{last_name[0]} → Erster Buchstabe Nachname
{first_name[0:3]} → Erste 3 Buchstaben Vorname
{last_name[0:3]} → Erste 3 Buchstaben Nachname`

function detectSpecialChars(rows: Record<string, string>[], firstNameCol: string | null, lastNameCol: string | null): string[] {
  const chars = new Set<string>()
  for (const row of rows) {
    const firstName = firstNameCol ? row[firstNameCol] || '' : ''
    const lastName = lastNameCol ? row[lastNameCol] || '' : ''
    const combined = firstName + lastName
    for (const char of combined) {
      if (SPECIAL_CHAR_DEFAULTS[char]) {
        chars.add(char)
      }
    }
  }
  return Array.from(chars).sort()
}

function buildColumnMappingFromDetected(
  fileColumns: string[],
  detectedMapping: Record<string, DetectedMappingEntry>
): ColumnMapping[] {
  const mapping: ColumnMapping[] = fileColumns.map(col => {
    const detected = Object.entries(detectedMapping).find(
      ([_, info]) => info.column === col
    )
    if (detected) {
      return {
        fileColumn: col,
        systemField: detected[0],
        confidence: detected[1].confidence,
        isAutoDetected: true,
        split_mode: detected[1].split_mode,
      }
    }
    return {
      fileColumn: col,
      systemField: null,
      confidence: 0,
      isAutoDetected: false,
      split_mode: null,
    }
  })
  return mapping
}

function buildColumnMappingDict(
  columnMapping: ColumnMapping[]
): Record<string, string> {
  const dict: Record<string, string> = {}
  for (const m of columnMapping) {
    if (m.systemField) {
      dict[m.systemField] = String(m.fileColumn ?? '')
    }
  }
  return dict
}

function importReducer(state: ImportState, action: ImportAction): ImportState {
  switch (action.type) {
    case 'SET_ROLES':
      return { ...state, roles: action.payload }
    case 'SET_TARGET_ROLE':
      return { ...state, targetRole: action.payload }
    case 'UPLOAD_START':
      return { ...state, uploading: true, error: null }
    case 'UPLOAD_SUCCESS':
      return {
        ...state,
        uploading: false,
        uploadedFileId: action.payload.fileId,
        fileName: action.payload.fileName,
        step: 'editor',
        loading: true,
        teacherCodeModalDismissed: false,
        teacherCodeGenerationConfig: null,
        showTeacherCodeGenerationModal: false,
        teacherCodeSuggestions: null,
      }
    case 'PARSE_SUCCESS':
      const parseMapping = buildColumnMappingFromDetected(
        action.payload.file_columns,
        action.payload.detected_mapping
      )
      const parseSplitMode = parseMapping.find(m => m.split_mode)?.split_mode
      const validSplitMode = parseSplitMode === 'last_first' || parseSplitMode === 'first_last' ? parseSplitMode : null
      const stats = action.payload.stats
      const firstNameCol = parseMapping.find(m => m.systemField === 'first_name')?.fileColumn ?? null
      const lastNameCol = parseMapping.find(m => m.systemField === 'last_name')?.fileColumn ?? null
      const detectedChars = detectSpecialChars(action.payload.raw_rows, firstNameCol, lastNameCol)
      const tcStats = action.payload.teacher_code_stats
      return {
        ...state,
        loading: false,
        needsPreview: true,
        fileColumns: action.payload.file_columns,
        rawRows: action.payload.raw_rows,
        sheets: action.payload.sheets,
        selectedSheet: action.payload.sheets[0] || '',
        totalRows: action.payload.total_rows,
        totalRowsActual: action.payload.total_rows,
        columnMapping: parseMapping,
        splitMode: validSplitMode,
        previewRows: action.payload.preview_rows,
        previewStats: stats ? { toCreate: stats.to_create, toUpdate: stats.to_update, errorCount: stats.error_count } : null,
        warnings: action.payload.warnings,
        combinedInfo: action.payload.combined_info,
        previewPage: 0,
        skipRowIndices: new Set(),
        editedCells: new Map(),
        detectedSpecialChars: detectedChars,
        needsTeacherCodeGeneration: action.payload.needs_teacher_code_generation || false,
        teacherCodeStats: tcStats ? {
          total_teacher_rows: tcStats.total_teacher_rows || 0,
          rows_with_file_code: tcStats.rows_with_file_code || 0,
          rows_with_generated_code: tcStats.rows_with_generated_code || 0,
          rows_with_conflicts: tcStats.rows_with_conflicts || 0,
          rows_without_code: tcStats.rows_without_code || 0,
        } : null,
        teacherCodeGenerationMethod: action.payload.teacher_code_generation_method || null,
        teacherCodeConflicts: action.payload.teacher_code_conflicts || [],
        previewFailed: false,
      }
    case 'PREVIEW_SUCCESS':
      const previewStats = action.payload.stats
      const tcStats2 = action.payload.teacher_code_stats
      return {
        ...state,
        loading: false,
        needsPreview: false,
        error: null,
        previewFailed: false,
        previewRows: action.payload.preview_rows,
        previewStats: previewStats ? { toCreate: previewStats.to_create, toUpdate: previewStats.to_update, errorCount: previewStats.error_count } : null,
        warnings: action.payload.warnings,
        totalRows: action.payload.total_rows,
        totalRowsActual: action.payload.total_rows,
        unmatchedSubjectTokens: action.payload.unmatched_subject_tokens || [],
        unmatchedBackgroundTokens: action.payload.unmatched_background_tokens || [],
        availableSubjects: action.payload.available_subjects || [],
        availableBackgrounds: action.payload.available_backgrounds || [],
        needsTokenMapping: action.payload.needs_token_mapping || false,
        needsTeacherCodeGeneration: action.payload.needs_teacher_code_generation || false,
        teacherCodeStats: tcStats2 ? {
          total_teacher_rows: tcStats2.total_teacher_rows || 0,
          rows_with_file_code: tcStats2.rows_with_file_code || 0,
          rows_with_generated_code: tcStats2.rows_with_generated_code || 0,
          rows_with_conflicts: tcStats2.rows_with_conflicts || 0,
          rows_without_code: tcStats2.rows_without_code || 0,
        } : state.teacherCodeStats,
        teacherCodeGenerationMethod: action.payload.teacher_code_generation_method || state.teacherCodeGenerationMethod,
        teacherCodeConflicts: action.payload.teacher_code_conflicts || [],
      }
    case 'UPDATE_MAPPING':
      const newMapping = state.columnMapping.map(m =>
        m.fileColumn === action.payload.fileColumn
          ? { ...m, systemField: action.payload.systemField, isAutoDetected: false }
          : m
      )
      return { ...state, columnMapping: newMapping, needsPreview: true }
    case 'UPDATE_SPLIT_MODE':
      return { ...state, splitMode: action.payload, needsPreview: true }
    case 'SELECT_SHEET':
      return { ...state, selectedSheet: action.payload, needsPreview: true }
    case 'SKIP_ROW':
      const newSkipSet = new Set(state.skipRowIndices)
      newSkipSet.add(action.payload)
      return { ...state, skipRowIndices: newSkipSet, needsPreview: true }
    case 'UNSKIP_ROW':
      const removedSkipSet = new Set(state.skipRowIndices)
      removedSkipSet.delete(action.payload)
      return { ...state, skipRowIndices: removedSkipSet, needsPreview: true }
    case 'EDIT_CELL':
      const newEditedCells = new Map(state.editedCells)
      newEditedCells.set(`${action.payload.rowIndex}${CELL_KEY_SEPARATOR}${action.payload.field}`, action.payload.value)
      return { ...state, editedCells: newEditedCells, needsPreview: true }
    case 'SET_UPDATE_EXISTING':
      return { ...state, updateExisting: action.payload }
    case 'SET_SKIP_DUPLICATES':
      return { ...state, skipDuplicates: action.payload }
    case 'EXECUTE_START':
      return { ...state, executing: true, error: null }
    case 'EXECUTE_SUCCESS':
      return { ...state, executing: false, execResult: action.payload, step: 'result' }
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        loading: false,
        uploading: false,
        executing: false,
        previewFailed: action.payload
          ? (action.previewFailed ?? state.previewFailed)
          : state.previewFailed,
      }
    case 'RETRY_PREVIEW':
      return { ...state, needsPreview: true, error: null, previewFailed: false, loading: true, previewRetryNonce: state.previewRetryNonce + 1 }
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_PREVIEW_PAGE':
      return { ...state, previewPage: action.payload }
    case 'RESET':
      return {
        ...initialState,
        roles: state.roles,
        targetRole: state.targetRole,
      }
    case 'GO_TO_STEP':
      return { ...state, step: action.payload }
    case 'SET_SUBJECT_TOKEN_MAPPING':
      return { ...state, subjectTokenMapping: action.payload, needsPreview: true }
    case 'SET_BACKGROUND_TOKEN_MAPPING':
      return { ...state, backgroundTokenMapping: action.payload, needsPreview: true }
    case 'SHOW_TOKEN_MAPPING_MODAL':
      return { ...state, showTokenMappingModal: action.payload }
    case 'SET_EMAIL_GENERATION_CONFIG':
      return { ...state, emailGenerationConfig: action.payload, needsPreview: true }
    case 'SHOW_EMAIL_GENERATION_MODAL':
      return { ...state, showEmailGenerationModal: action.payload }
    case 'SET_DETECTED_SPECIAL_CHARS':
      return { ...state, detectedSpecialChars: action.payload }
    case 'SET_ROLE_OVERRIDE':
      const newRoleOverrides = new Map(state.roleOverrides)
      newRoleOverrides.set(action.payload.rowIndex, action.payload.role)
      return { ...state, roleOverrides: newRoleOverrides, needsPreview: true }
    case 'CLEAR_ROLE_OVERRIDE':
      const clearedRoleOverrides = new Map(state.roleOverrides)
      clearedRoleOverrides.delete(action.payload)
      return { ...state, roleOverrides: clearedRoleOverrides, needsPreview: true }
    case 'SET_TEACHER_CODE_GENERATION_CONFIG':
      return { ...state, teacherCodeGenerationConfig: action.payload, needsPreview: true }
    case 'SHOW_TEACHER_CODE_GENERATION_MODAL':
      return { ...state, showTeacherCodeGenerationModal: action.payload }
    case 'SET_TEACHER_CODE_SUGGESTIONS':
      return { ...state, teacherCodeSuggestions: action.payload }
    case 'SET_TEACHER_CODE_STATS':
      return { ...state, teacherCodeStats: action.payload }
    case 'SET_TEACHER_CODE_CONFLICTS':
      return { ...state, teacherCodeConflicts: action.payload }
    case 'SET_OVERWRITE_EXISTING_CODES':
      return { ...state, overwriteExistingCodes: action.payload, needsPreview: true }
    case 'DISMISS_TEACHER_CODE_GENERATION_MODAL':
      return {
        ...state,
        showTeacherCodeGenerationModal: false,
        teacherCodeModalDismissed: true,
        teacherCodeGenerationConfig: null,
        overwriteExistingCodes: false,
        needsPreview: state.teacherCodeGenerationConfig !== null,
      }
    default:
      return state
  }
}

const initialState: ImportState = {
  step: 'upload',
  uploadedFileId: null,
  fileName: '',
  targetRole: 'teacher',
  fileColumns: [],
  rawRows: [],
  sheets: [],
  selectedSheet: '',
  totalRows: 0,
  totalRowsActual: 0,
  columnMapping: [],
  splitMode: null,
  previewRows: [],
  previewStats: null,
  warnings: [],
  skipRowIndices: new Set(),
  editedCells: new Map(),
  roleOverrides: new Map(),
  updateExisting: true,
  skipDuplicates: true,
  executing: false,
  execResult: null,
  loading: false,
  needsPreview: false,
  error: null,
  uploading: false,
  showRawData: false,
  previewPage: 0,
  roles: [],
  combinedInfo: null,
  unmatchedSubjectTokens: [],
  unmatchedBackgroundTokens: [],
  availableSubjects: [],
  availableBackgrounds: [],
  needsTokenMapping: false,
  subjectTokenMapping: {},
  backgroundTokenMapping: {},
  showTokenMappingModal: false,
  emailGenerationConfig: null,
  showEmailGenerationModal: false,
  detectedSpecialChars: [],
  teacherCodeGenerationConfig: null,
  showTeacherCodeGenerationModal: false,
  teacherCodeSuggestions: null,
  teacherCodeStats: null,
  teacherCodeConflicts: [],
  needsTeacherCodeGeneration: false,
  teacherCodeGenerationMethod: null,
  overwriteExistingCodes: false,
  teacherCodeModalDismissed: false,
  previewFailed: false,
  previewRetryNonce: 0,
}

interface PersonalImportEditorProps {
  onImportComplete?: (result: ImportExecuteResult) => void
  onCancel?: () => void
}

export function PersonalImportEditor({ onImportComplete, onCancel }: PersonalImportEditorProps) {
  const [state, dispatch] = useReducer(importReducer, initialState)
  const previewDebounceRef = useRef<TimeoutRef>(null)
  const [showRowBackgroundModal, setShowRowBackgroundModal] = useState<{ rowIndex: number; errors: string[] } | null>(null)

  const fetchRoles = useCallback(async () => {
    try {
      const res = await handleApiRequest<{ roles: RoleEntry[]; permissions: Record<string, string[]> }>(
        apiClient.get('/api/v1/bulk-import/users/roles')
      )
      dispatch({ type: 'SET_ROLES', payload: res.roles })
    } catch {
      dispatch({
        type: 'SET_ROLES',
        payload: [
          { role: 'teacher', label: 'Lehrer' },
          { role: 'hr', label: 'HR' },
          { role: 'admin', label: 'Admin' },
        ],
      })
    }
  }, [])

  useEffect(() => {
    fetchRoles()
  }, [fetchRoles])

  const fetchTeacherCodeSuggestions = useCallback(async () => {
    try {
      const res = await handleApiRequest(apiClient.get('/api/v1/teacher-codes/stats'))
      dispatch({ type: 'SET_TEACHER_CODE_SUGGESTIONS', payload: {
        activePattern: res.active_pattern,
        autoInferredPattern: res.auto_inferred_pattern,
        availableRules: res.available_rules || [],
        recommendation: res.recommendation || 'rule',
        withCode: res.with_code || 0,
        withoutCode: res.without_code || 0,
      }})
    } catch {
      dispatch({ type: 'SET_TEACHER_CODE_SUGGESTIONS', payload: {
        activePattern: null,
        autoInferredPattern: null,
        availableRules: [],
        recommendation: 'rule',
        withCode: 0,
        withoutCode: 0,
      }})
    }
  }, [])

  useEffect(() => {
    if (state.needsTeacherCodeGeneration && !state.teacherCodeSuggestions && state.step === 'editor') {
      fetchTeacherCodeSuggestions()
    }
  }, [state.needsTeacherCodeGeneration, state.step, state.teacherCodeSuggestions, fetchTeacherCodeSuggestions])

  useEffect(() => {
    const teacherCodeMapped = state.columnMapping.some(m => m.systemField === 'teacher_code')
    if (
      state.needsTeacherCodeGeneration &&
      state.teacherCodeSuggestions &&
      !state.teacherCodeGenerationConfig &&
      !state.showTeacherCodeGenerationModal &&
      !state.teacherCodeModalDismissed &&
      !teacherCodeMapped
    ) {
      dispatch({ type: 'SHOW_TEACHER_CODE_GENERATION_MODAL', payload: true })
    }
  }, [state.needsTeacherCodeGeneration, state.teacherCodeSuggestions, state.teacherCodeGenerationConfig, state.showTeacherCodeGenerationModal, state.teacherCodeModalDismissed, state.columnMapping])

  useEffect(() => {
    return () => {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current)
    }
  }, [])

  const handleFileUpload = useCallback(async (file: File) => {
    dispatch({ type: 'UPLOAD_START' })
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('file_category', 'user_import')
      const data = await handleApiRequest<{ id: string }>(
        apiClient.post('/api/v1/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }),
      )
      dispatch({ type: 'UPLOAD_SUCCESS', payload: { fileId: data.id, fileName: file.name } })
      fetchPreview(data.id, state.targetRole, null)
    } catch (err) {
      const msg = extractErrorMessage(err, { loadError: 'Upload fehlgeschlagen' })
      dispatch({ type: 'SET_ERROR', payload: msg })
    }
  }, [state.targetRole])

  const fetchPreview = useCallback(async (
    fileId: string,
    targetRole: string,
    columnMapping: Record<string, string> | null,
    skipRowIndices: number[] = [],
    editedCells: Array<{ row_index: number; field: string; value: string }> = [],
    subjectTokenMapping: Record<string, string[]> = {},
    backgroundTokenMapping: Record<string, string[]> = {},
    emailGenerationConfig: EmailGenerationConfig | null = null,
    roleOverrides: Record<number, string> = {},
    teacherCodeGeneration: TeacherCodeGenerationConfig | null = null,
    overwriteExistingCodes: boolean = false,
  ) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    try {
      const res = await handleApiRequest<PreviewResult>(
        apiClient.post('/api/v1/bulk-import/users/preview', {
          uploaded_file_id: fileId,
          target_role: targetRole,
          column_mapping: columnMapping,
          split_mode: state.splitMode,
          sheet_name: state.selectedSheet || undefined,
          skip_row_indices: skipRowIndices,
          edited_cells: editedCells,
          subject_token_mapping: subjectTokenMapping,
          background_token_mapping: backgroundTokenMapping,
          email_generation_config: emailGenerationConfig ? {
            pattern: emailGenerationConfig.pattern,
            domain: emailGenerationConfig.domain,
            char_mappings: emailGenerationConfig.charMappings,
            lowercase: emailGenerationConfig.lowercase,
          } : null,
          role_overrides: Object.fromEntries(
            Object.entries(roleOverrides).map(([k, v]) => [String(k), v])
          ),
          teacher_code_generation: teacherCodeGeneration && (teacherCodeGeneration.method === 'rule' || teacherCodeGeneration.method === 'pattern') ? {
            method: teacherCodeGeneration.method,
            rule_type: teacherCodeGeneration.ruleType,
            pattern_syntax: teacherCodeGeneration.patternSyntax,
          } : null,
          overwrite_existing_codes: overwriteExistingCodes,
        })
      )
      if (columnMapping) {
        dispatch({ type: 'PREVIEW_SUCCESS', payload: res })
      } else {
        dispatch({ type: 'PARSE_SUCCESS', payload: res })
      }
    } catch (err) {
      const msg = extractErrorMessage(err, { loadError: 'Preview fehlgeschlagen' })
      dispatch({ type: 'SET_ERROR', payload: msg, previewFailed: true })
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [state.splitMode, state.selectedSheet])

  useEffect(() => {
    if (state.uploadedFileId && state.step === 'editor' && state.needsPreview && state.columnMapping.length > 0) {
      const required = REQUIRED_FIELDS[state.targetRole] || []
      const emailMapped = state.columnMapping.some(m => m.systemField === 'email')
      const firstNameMapped = state.columnMapping.some(m => m.systemField === 'first_name')
      const lastNameMapped = state.columnMapping.some(m => m.systemField === 'last_name')
      const canGenerateEmail = !emailMapped && firstNameMapped && lastNameMapped
      const hasRequiredFields = required.every(f => {
        if (f === 'email') {
          return emailMapped || (canGenerateEmail && state.emailGenerationConfig !== null)
        }
        return state.columnMapping.some(m => m.systemField === f)
      })
      if (!hasRequiredFields) {
        dispatch({ type: 'SET_LOADING', payload: false })
        return
      }
      if (previewDebounceRef.current) {
        clearTimeout(previewDebounceRef.current)
      }
      previewDebounceRef.current = setTimeout(() => {
        const mappingDict = buildColumnMappingDict(state.columnMapping)
        const skipRows = Array.from(state.skipRowIndices)
        const edits: Array<{ row_index: number; field: string; value: string }> = Array.from(state.editedCells.entries())
          .map(([key, value]) => {
            const parts = key.split(CELL_KEY_SEPARATOR)
            const rowIndexStr = parts[0] ?? '0'
            const field = parts.slice(1).join(CELL_KEY_SEPARATOR)
            return { row_index: parseInt(rowIndexStr, 10), field, value }
          })
          .filter(e => e.field !== '')
        if (state.uploadedFileId) {
          dispatch({ type: 'SET_LOADING', payload: true })
          const roleOverridesDict: Record<number, string> = {}
          state.roleOverrides.forEach((role, idx) => { roleOverridesDict[idx] = role })
          fetchPreview(state.uploadedFileId, state.targetRole, mappingDict, skipRows, edits, state.subjectTokenMapping, state.backgroundTokenMapping, state.emailGenerationConfig, roleOverridesDict, state.teacherCodeGenerationConfig, state.overwriteExistingCodes)
        }
      }, 300)
    }
  }, [state.columnMapping, state.skipRowIndices, state.editedCells, state.uploadedFileId, state.targetRole, state.step, state.needsPreview, fetchPreview, state.subjectTokenMapping, state.backgroundTokenMapping, state.emailGenerationConfig, state.roleOverrides, state.teacherCodeGenerationConfig, state.overwriteExistingCodes, state.previewRetryNonce])

  const lastSheetRef = useRef('')
  useEffect(() => {
    if (state.uploadedFileId && state.step === 'editor' && state.selectedSheet && state.sheets.length > 1) {
      if (lastSheetRef.current && lastSheetRef.current !== state.selectedSheet) {
        dispatch({ type: 'SET_LOADING', payload: true })
        fetchPreview(state.uploadedFileId, state.targetRole, null)
      }
      lastSheetRef.current = state.selectedSheet
    }
  }, [state.selectedSheet, state.uploadedFileId, state.step, state.targetRole, fetchPreview])

  const handleExecute = useCallback(async () => {
    if (!state.uploadedFileId) return
    dispatch({ type: 'EXECUTE_START' })
    try {
      const mappingDict = buildColumnMappingDict(state.columnMapping)
      const skipRows = Array.from(state.skipRowIndices)
      const edits: Array<{ row_index: number; field: string; value: string }> = Array.from(state.editedCells.entries())
        .map(([key, value]) => {
          const parts = key.split(CELL_KEY_SEPARATOR)
          const rowIndexStr = parts[0] ?? '0'
          const field = parts.slice(1).join(CELL_KEY_SEPARATOR)
          return { row_index: parseInt(rowIndexStr, 10), field, value }
        })
        .filter(e => e.field !== '')
      const roleOverridesDict: Record<number, string> = {}
      state.roleOverrides.forEach((role, idx) => { roleOverridesDict[idx] = role })
      const res = await handleApiRequest<ImportExecuteResult>(
        apiClient.post('/api/v1/bulk-import/users/execute', {
          uploaded_file_id: state.uploadedFileId,
          target_role: state.targetRole,
          column_mapping: mappingDict,
          split_mode: state.splitMode,
          sheet_name: state.selectedSheet || undefined,
          skip_row_indices: skipRows,
          edited_cells: edits,
          update_existing: state.updateExisting,
          skip_duplicates: state.skipDuplicates,
          subject_token_mapping: state.subjectTokenMapping,
          background_token_mapping: state.backgroundTokenMapping,
          email_generation_config: state.emailGenerationConfig ? {
            pattern: state.emailGenerationConfig.pattern,
            domain: state.emailGenerationConfig.domain,
            char_mappings: state.emailGenerationConfig.charMappings,
            lowercase: state.emailGenerationConfig.lowercase,
          } : null,
          role_overrides: roleOverridesDict,
          teacher_code_generation: state.teacherCodeGenerationConfig && (state.teacherCodeGenerationConfig.method === 'rule' || state.teacherCodeGenerationConfig.method === 'pattern') ? {
            method: state.teacherCodeGenerationConfig.method,
            rule_type: state.teacherCodeGenerationConfig.ruleType,
            pattern_syntax: state.teacherCodeGenerationConfig.patternSyntax,
          } : null,
          overwrite_existing_codes: state.overwriteExistingCodes,
        })
      )
      dispatch({ type: 'EXECUTE_SUCCESS', payload: res })
      onImportComplete?.(res)
    } catch (err) {
      const msg = extractErrorMessage(err, { loadError: 'Import fehlgeschlagen' })
      dispatch({ type: 'SET_ERROR', payload: msg })
    }
  }, [state.uploadedFileId, state.targetRole, state.columnMapping, state.splitMode, state.selectedSheet, state.skipRowIndices, state.editedCells, state.updateExisting, state.skipDuplicates, state.subjectTokenMapping, state.backgroundTokenMapping, state.emailGenerationConfig, state.roleOverrides, state.teacherCodeGenerationConfig, state.overwriteExistingCodes, onImportComplete])

  const handleMappingChange = useCallback((fileColumn: string, systemField: string | null) => {
    dispatch({ type: 'UPDATE_MAPPING', payload: { fileColumn, systemField } })
  }, [])

  const handleSplitModeChange = useCallback((mode: 'last_first' | 'first_last' | null) => {
    dispatch({ type: 'UPDATE_SPLIT_MODE', payload: mode })
  }, [])

  const handleSkipRow = useCallback((rowIndex: number) => {
    dispatch({ type: 'SKIP_ROW', payload: rowIndex })
  }, [])

  const handleUnskipRow = useCallback((rowIndex: number) => {
    dispatch({ type: 'UNSKIP_ROW', payload: rowIndex })
  }, [])

  const handleEditCell = useCallback((rowIndex: number, field: string, value: string) => {
    dispatch({ type: 'EDIT_CELL', payload: { rowIndex, field, value } })
  }, [])

  const requiredFields = REQUIRED_FIELDS[state.targetRole] || []
  const mappedFields = state.columnMapping.filter(m => m.systemField).map(m => m.systemField!)
  const emailMapped = mappedFields.includes('email')
  const emailCovered = emailMapped || state.emailGenerationConfig !== null
  const missingRequired = requiredFields.filter(f => {
    if (f === 'email') return !emailCovered
    return !mappedFields.includes(f)
  })
  const errorThreshold = state.totalRowsActual > 0 ? state.totalRowsActual * 0.9 : 50
  const canImport: boolean = missingRequired.length === 0 && state.previewStats !== null && state.previewStats.errorCount < errorThreshold && !state.loading && !state.needsPreview && !state.previewFailed && !state.executing

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {(state.error || (state.previewFailed && state.step === 'editor')) && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded flex items-center justify-between gap-3">
          <span className="text-sm whitespace-pre-wrap">{state.error || 'Vorschau fehlgeschlagen. Spaltenzuordnung prüfen und erneut versuchen.'}</span>
          <div className="flex items-center gap-2 shrink-0">
            {state.previewFailed && state.uploadedFileId && state.step === 'editor' && (
              <button
                type="button"
                onClick={() => dispatch({ type: 'RETRY_PREVIEW' })}
                className="px-2 py-1 text-xs bg-red-700 text-white rounded hover:bg-red-800"
              >
                Erneut versuchen
              </button>
            )}
            {state.error && (
              <button onClick={() => dispatch({ type: 'SET_ERROR', payload: null })} className="text-red-500 hover:text-red-700">
                ✕
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          {state.step !== 'upload' && (
            <button
              onClick={() => dispatch({ type: 'GO_TO_STEP', payload: state.step === 'result' ? 'editor' : 'upload' })}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              ← Zurück
            </button>
          )}
          <h1 className="text-lg font-semibold">
            {state.step === 'upload' ? 'Personal Import' : state.fileName}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Rolle:</label>
          <select
            value={state.targetRole}
            onChange={e => dispatch({ type: 'SET_TARGET_ROLE', payload: e.target.value })}
            disabled={state.step !== 'upload'}
            className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50"
          >
            {state.roles.map(r => (
              <option key={r.role} value={r.role}>{r.label}</option>
            ))}
          </select>
        </div>
      </div>

      {state.step === 'upload' && (
        <UploadStep
          uploading={state.uploading}
          onFileUpload={handleFileUpload}
        />
      )}

      {state.step === 'editor' && !state.showTeacherCodeGenerationModal && (state.teacherCodeModalDismissed || state.teacherCodeGenerationConfig) && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between gap-3">
          <p className="text-sm text-blue-800">
            {state.teacherCodeGenerationConfig
              ? 'Lehrer-Kürzel werden automatisch generiert.'
              : state.columnMapping.some(m => m.systemField === 'teacher_code')
                ? 'Kürzel werden aus der Import-Datei übernommen.'
                : 'Keine Kürzel-Generierung. Spalte «Kürzel» im Mapping zuordnen, um Kürzel aus der Liste zu importieren.'}
          </p>
          <button
            type="button"
            onClick={() => {
              if (!state.teacherCodeSuggestions) fetchTeacherCodeSuggestions()
              dispatch({ type: 'SHOW_TEACHER_CODE_GENERATION_MODAL', payload: true })
            }}
            className="text-xs text-blue-600 hover:underline shrink-0"
          >
            Ändern
          </button>
        </div>
      )}

      {state.step === 'editor' && (
        <EditorStep
          rawRows={state.rawRows}
          columnMapping={state.columnMapping}
          splitMode={state.splitMode}
          sheets={state.sheets}
          selectedSheet={state.selectedSheet}
          onSelectSheet={(s) => dispatch({ type: 'SELECT_SHEET', payload: s })}
          onMappingChange={handleMappingChange}
          onSplitModeChange={handleSplitModeChange}
          previewRows={state.previewRows}
          previewStats={state.previewStats}
          warnings={state.warnings}
          totalRows={state.totalRows}
          totalRowsActual={state.totalRowsActual}
          loading={state.loading}
          skipRowIndices={state.skipRowIndices}
          editedCells={state.editedCells}
          onSkipRow={handleSkipRow}
          onUnskipRow={handleUnskipRow}
          onEditCell={handleEditCell}
          roleOverrides={state.roleOverrides}
          roles={state.roles}
          targetRole={state.targetRole}
          onRoleOverrideChange={(rowIndex, role) => dispatch({ type: 'SET_ROLE_OVERRIDE', payload: { rowIndex, role } })}
          onRoleOverrideClear={(rowIndex) => dispatch({ type: 'CLEAR_ROLE_OVERRIDE', payload: rowIndex })}
          updateExisting={state.updateExisting}
          skipDuplicates={state.skipDuplicates}
          onUpdateExistingChange={(v) => dispatch({ type: 'SET_UPDATE_EXISTING', payload: v })}
          onSkipDuplicatesChange={(v) => dispatch({ type: 'SET_SKIP_DUPLICATES', payload: v })}
          availableFields={AVAILABLE_FIELDS[state.targetRole] || []}
          requiredFields={requiredFields}
          missingRequired={missingRequired}
          canImport={canImport}
          onExecute={handleExecute}
          executing={state.executing}
          needsTokenMapping={state.needsTokenMapping}
          unmatchedSubjectTokens={state.unmatchedSubjectTokens}
          unmatchedBackgroundTokens={state.unmatchedBackgroundTokens}
          availableSubjects={state.availableSubjects}
          availableBackgrounds={state.availableBackgrounds}
          subjectTokenMapping={state.subjectTokenMapping}
          backgroundTokenMapping={state.backgroundTokenMapping}
          showTokenMappingModal={state.showTokenMappingModal}
          onShowTokenMappingModal={(v) => dispatch({ type: 'SHOW_TOKEN_MAPPING_MODAL', payload: v })}
          onSubjectTokenMappingChange={(m) => dispatch({ type: 'SET_SUBJECT_TOKEN_MAPPING', payload: m })}
          onBackgroundTokenMappingChange={(m) => dispatch({ type: 'SET_BACKGROUND_TOKEN_MAPPING', payload: m })}
          emailGenerationConfig={state.emailGenerationConfig}
          showEmailGenerationModal={state.showEmailGenerationModal}
          detectedSpecialChars={state.detectedSpecialChars}
          onShowEmailGenerationModal={(v) => dispatch({ type: 'SHOW_EMAIL_GENERATION_MODAL', payload: v })}
          onEmailGenerationConfigChange={(c) => dispatch({ type: 'SET_EMAIL_GENERATION_CONFIG', payload: c })}
          showRowBackgroundModal={showRowBackgroundModal}
          onShowRowBackgroundModal={(rowIndex, errors) => {
            if (rowIndex === -1) setShowRowBackgroundModal(null)
            else setShowRowBackgroundModal({ rowIndex, errors })
          }}
          onRowBackgroundSelect={(_rowIndex, background) => {
            const currentMapping = state.backgroundTokenMapping
            const newMapping = { ...currentMapping }
            const existingErrors = showRowBackgroundModal?.errors || []
            for (const error of existingErrors) {
              const tokenMatch = error.match(/Hintergrund '([^']+)' nicht gefunden/)
              if (tokenMatch && tokenMatch[1]) {
                const token = tokenMatch[1]
                newMapping[token] = [background]
              } else {
                console.warn(`Could not extract token from error: ${error}`)
              }
            }
            dispatch({ type: 'SET_BACKGROUND_TOKEN_MAPPING', payload: newMapping })
            setShowRowBackgroundModal(null)
          }}
        />
      )}

      {state.showTeacherCodeGenerationModal && (
        <TeacherCodeGenerationModal
          suggestions={state.teacherCodeSuggestions}
          stats={state.teacherCodeStats}
          hasFileCodeColumn={state.columnMapping.some(m => m.systemField === 'teacher_code')}
          onSkip={() => {
            dispatch({ type: 'DISMISS_TEACHER_CODE_GENERATION_MODAL' })
          }}
          onApply={(config, overwrite) => {
            if (!config) {
              dispatch({ type: 'DISMISS_TEACHER_CODE_GENERATION_MODAL' })
              return
            }
            dispatch({ type: 'SET_TEACHER_CODE_GENERATION_CONFIG', payload: config })
            dispatch({ type: 'SET_OVERWRITE_EXISTING_CODES', payload: overwrite })
            dispatch({ type: 'SHOW_TEACHER_CODE_GENERATION_MODAL', payload: false })
          }}
        />
      )}

      {state.step === 'result' && state.execResult && (
        <ResultStep
          result={state.execResult}
          onNewImport={() => dispatch({ type: 'RESET' })}
          onCancel={onCancel}
        />
      )}
    </div>
  )
}

function UploadStep({ uploading, onFileUpload }: { uploading: boolean; onFileUpload: (file: File) => void }) {
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) onFileUpload(file)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) onFileUpload(file)
  }

  return (
    <div
      className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-blue-400 transition-colors"
      onDrop={handleDrop}
      onDragOver={e => e.preventDefault()}
    >
      {uploading ? (
        <div className="text-gray-500">
          <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-blue-500 rounded-full mx-auto mb-2" />
          Datei wird hochgeladen...
        </div>
      ) : (
        <>
          <div className="w-12 h-12 mx-auto mb-4 text-gray-400">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" />
            </svg>
          </div>
          <p className="text-gray-600 mb-2">CSV oder Excel-Datei hierher ziehen</p>
          <p className="text-gray-400 text-sm mb-4">oder</p>
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleChange}
            className="hidden"
            id="file-upload"
          />
          <label
            htmlFor="file-upload"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer text-sm"
          >
            Datei auswählen
          </label>
          <p className="text-xs text-gray-400 mt-4">Unterstützte Formate: CSV (.csv), Excel (.xlsx, .xls)</p>
        </>
      )}
    </div>
  )
}

function EditorStep({
  rawRows,
  columnMapping,
  splitMode,
  sheets,
  selectedSheet,
  onSelectSheet,
  onMappingChange,
  onSplitModeChange,
  previewRows,
  previewStats,
  warnings,
  totalRowsActual,
  loading,
  skipRowIndices,
  editedCells,
  roleOverrides,
  roles,
  targetRole,
  onSkipRow,
  onUnskipRow,
  onEditCell,
  onRoleOverrideChange,
  onRoleOverrideClear,
  updateExisting,
  skipDuplicates,
  onUpdateExistingChange,
  onSkipDuplicatesChange,
  availableFields,
  requiredFields,
  missingRequired,
  canImport,
  onExecute,
  executing,
  needsTokenMapping,
  unmatchedSubjectTokens,
  unmatchedBackgroundTokens,
  availableSubjects,
  availableBackgrounds,
  subjectTokenMapping,
  backgroundTokenMapping,
  showTokenMappingModal,
  onShowTokenMappingModal,
  onSubjectTokenMappingChange,
  onBackgroundTokenMappingChange,
  emailGenerationConfig,
  showEmailGenerationModal,
  detectedSpecialChars,
  onShowEmailGenerationModal,
  onEmailGenerationConfigChange,
  showRowBackgroundModal,
  onShowRowBackgroundModal,
  onRowBackgroundSelect,
}: {
  rawRows: Record<string, string>[]
  columnMapping: ColumnMapping[]
  splitMode: 'last_first' | 'first_last' | null
  sheets: string[]
  selectedSheet: string
  onSelectSheet: (sheet: string) => void
  onMappingChange: (fileColumn: string, systemField: string | null) => void
  onSplitModeChange: (mode: 'last_first' | 'first_last' | null) => void
  previewRows: PreviewRow[]
  previewStats: { toCreate: number; toUpdate: number; errorCount: number } | null
  warnings: string[]
  totalRows: number
  totalRowsActual: number
  loading: boolean
  skipRowIndices: Set<number>
  editedCells: Map<string, string>
  roleOverrides: Map<number, string>
  roles: RoleEntry[]
  targetRole: string
  onSkipRow: (rowIndex: number) => void
  onUnskipRow: (rowIndex: number) => void
  onEditCell: (rowIndex: number, field: string, value: string) => void
  onRoleOverrideChange: (rowIndex: number, role: string) => void
  onRoleOverrideClear: (rowIndex: number) => void
  updateExisting: boolean
  skipDuplicates: boolean
  onUpdateExistingChange: (value: boolean) => void
  onSkipDuplicatesChange: (value: boolean) => void
  availableFields: string[]
  requiredFields: string[]
  missingRequired: string[]
  canImport: boolean
  onExecute: () => void
  executing: boolean
  needsTokenMapping: boolean
  unmatchedSubjectTokens: string[]
  unmatchedBackgroundTokens: string[]
  availableSubjects: string[]
  availableBackgrounds: string[]
  subjectTokenMapping: Record<string, string[]>
  backgroundTokenMapping: Record<string, string[]>
  showTokenMappingModal: boolean
  onShowTokenMappingModal: (show: boolean) => void
  onSubjectTokenMappingChange: (mapping: Record<string, string[]>) => void
  onBackgroundTokenMappingChange: (mapping: Record<string, string[]>) => void
  emailGenerationConfig: EmailGenerationConfig | null
  showEmailGenerationModal: boolean
  detectedSpecialChars: string[]
  onShowEmailGenerationModal: (show: boolean) => void
  onEmailGenerationConfigChange: (config: EmailGenerationConfig) => void
  showRowBackgroundModal: { rowIndex: number; errors: string[] } | null
  onShowRowBackgroundModal: ((rowIndex: number, errors: string[]) => void) | null
  onRowBackgroundSelect: (rowIndex: number, background: string) => void
}) {
  const hasCombinedName = columnMapping.some(m => m.split_mode !== null && m.systemField === 'last_name')
  const [localSubjectMapping, setLocalSubjectMapping] = useState<Record<string, string[]>>(() => {
    const result: Record<string, string[]> = {}
    for (const [k, v] of Object.entries(subjectTokenMapping)) {
      result[k] = typeof v === 'string' ? [v] : v
    }
    return result
  })
  const [localBackgroundMapping, setLocalBackgroundMapping] = useState<Record<string, string[]>>(() => {
    const result: Record<string, string[]> = {}
    for (const [k, v] of Object.entries(backgroundTokenMapping)) {
      result[k] = typeof v === 'string' ? [v] : v
    }
    return result
  })
  const [manualSubjectTokens, setManualSubjectTokens] = useState<string[]>([])
  const [manualBackgroundTokens, setManualBackgroundTokens] = useState<string[]>([])
  const [newSubjectToken, setNewSubjectToken] = useState('')
  const [newBackgroundToken, setNewBackgroundToken] = useState('')
  const [selectedRowBackground, setSelectedRowBackground] = useState<string>('')

  const allSubjectTokens = [...unmatchedSubjectTokens, ...manualSubjectTokens]
  const allBackgroundTokens = [...unmatchedBackgroundTokens, ...manualBackgroundTokens]

  const addSubjectToken = () => {
    if (newSubjectToken && !allSubjectTokens.includes(newSubjectToken)) {
      setManualSubjectTokens([...manualSubjectTokens, newSubjectToken])
      setNewSubjectToken('')
    }
  }

  const removeSubjectToken = (token: string) => {
    setManualSubjectTokens(manualSubjectTokens.filter(t => t !== token))
    const newMapping = { ...localSubjectMapping }
    delete newMapping[token]
    setLocalSubjectMapping(newMapping)
  }

  const toggleSubjectSelection = (token: string, subject: string) => {
    const current = localSubjectMapping[token] || []
    const newSelection = current.includes(subject)
      ? current.filter(s => s !== subject)
      : [...current, subject]
    setLocalSubjectMapping({ ...localSubjectMapping, [token]: newSelection })
  }

  const addBackgroundToken = () => {
    if (newBackgroundToken && !allBackgroundTokens.includes(newBackgroundToken)) {
      setManualBackgroundTokens([...manualBackgroundTokens, newBackgroundToken])
      setNewBackgroundToken('')
    }
  }

  const removeBackgroundToken = (token: string) => {
    setManualBackgroundTokens(manualBackgroundTokens.filter(t => t !== token))
    const newMapping = { ...localBackgroundMapping }
    delete newMapping[token]
    setLocalBackgroundMapping(newMapping)
  }

  const toggleBackgroundSelection = (token: string, background: string) => {
    const current = localBackgroundMapping[token] || []
    const newSelection = current.includes(background)
      ? current.filter(b => b !== background)
      : [...current, background]
    setLocalBackgroundMapping({ ...localBackgroundMapping, [token]: newSelection })
  }
  const [localEmailPattern, setLocalEmailPattern] = useState<string>(emailGenerationConfig?.pattern || '{first_name}.{last_name}')
  const [localEmailDomain, setLocalEmailDomain] = useState<string>(emailGenerationConfig?.domain || '')
  const [localCharMappings, setLocalCharMappings] = useState<Record<string, string>>(emailGenerationConfig?.charMappings || {})
  const [localLowercase, setLocalLowercase] = useState<boolean>(emailGenerationConfig?.lowercase ?? true)
  const [showCustomPatternInput, setShowCustomPatternInput] = useState<boolean>(false)
  const [emailModalDismissed, setEmailModalDismissed] = useState<boolean>(false)
  const [fetchedDomain, setFetchedDomain] = useState<string | null>(null)

  const emailMapped = columnMapping.some(m => m.systemField === 'email')
  const firstNameMapped = columnMapping.some(m => m.systemField === 'first_name')
  const lastNameMapped = columnMapping.some(m => m.systemField === 'last_name')
  const canGenerateEmail = !emailMapped && firstNameMapped && lastNameMapped

  useEffect(() => {
    if (showEmailGenerationModal && !fetchedDomain && !emailGenerationConfig?.domain) {
      apiClient.get('/api/v1/bulk-import/users/email-domain')
        .then(res => res.data)
        .then(data => {
          if (data.domain) {
            setFetchedDomain(data.domain)
            setLocalEmailDomain(data.domain)
          }
        })
        .catch(() => {})
    }
  }, [showEmailGenerationModal, fetchedDomain, emailGenerationConfig?.domain])

  useEffect(() => {
    if (!emailMapped && firstNameMapped && lastNameMapped && !emailGenerationConfig && !showEmailGenerationModal && !emailModalDismissed) {
      onShowEmailGenerationModal(true)
    }
  }, [emailMapped, firstNameMapped, lastNameMapped, emailGenerationConfig, showEmailGenerationModal, onShowEmailGenerationModal, emailModalDismissed])

  return (
    <div className="space-y-6">
      {warnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-sm font-medium text-yellow-800 mb-1">Warnungen:</p>
          {warnings.map((w, i) => (
            <p key={i} className="text-xs text-yellow-700">{w}</p>
          ))}
        </div>
      )}

      {needsTokenMapping && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm font-medium text-red-800 mb-1">Token-Mapping erforderlich:</p>
          <p className="text-xs text-red-700 mb-2">
            {unmatchedSubjectTokens.length > 0 && `${unmatchedSubjectTokens.length} Fächer-Tokens nicht erkannt: ${unmatchedSubjectTokens.slice(0, 5).join(', ')}${unmatchedSubjectTokens.length > 5 ? '...' : ''}`}
            {unmatchedBackgroundTokens.length > 0 && ` · ${unmatchedBackgroundTokens.length} Hintergrund-Tokens nicht erkannt: ${unmatchedBackgroundTokens.slice(0, 5).join(', ')}${unmatchedBackgroundTokens.length > 5 ? '...' : ''}`}
          </p>
          <button
            onClick={() => onShowTokenMappingModal(true)}
            className="text-xs px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Token-Mapping öffnen
          </button>
        </div>
      )}

      {showTokenMappingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Token-Mapping</h3>
            
            {allSubjectTokens.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Fächer ({allSubjectTokens.length} Tokens) — mehrere möglich</h4>
                <div className="space-y-2">
                  {allSubjectTokens.map(token => (
                    <div key={token} className="border rounded p-2 bg-gray-50">
                      <div className="flex items-center gap-2 mb-1">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">{token}</code>
                        {manualSubjectTokens.includes(token) && (
                          <span className="text-xs text-blue-600">(manuell)</span>
                        )}
                        {(localSubjectMapping[token]?.length || 0) > 1 && (
                          <span className="text-xs text-green-600">({localSubjectMapping[token]?.length} ausgewählt)</span>
                        )}
                        {manualSubjectTokens.includes(token) && (
                          <button
                            onClick={() => removeSubjectToken(token)}
                            className="text-xs text-red-500 hover:text-red-700 ml-auto"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {availableSubjects.map(s => (
                          <label key={s} className="flex items-center gap-1 text-xs cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(localSubjectMapping[token] || []).includes(s)}
                              onChange={() => toggleSubjectSelection(token, s)}
                              className="w-3 h-3"
                            />
                            <span className={(localSubjectMapping[token] || []).includes(s) ? 'font-medium text-green-700' : 'text-gray-600'}>
                              {s}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4 border-t pt-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Token manuell hinzufügen</h4>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newSubjectToken}
                  onChange={e => setNewSubjectToken(e.target.value)}
                  placeholder="Neues Fach-Token"
                  className="flex-1 px-2 py-1 border rounded text-sm"
                />
                <button
                  onClick={addSubjectToken}
                  disabled={!newSubjectToken}
                  className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  + Fach
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newBackgroundToken}
                  onChange={e => setNewBackgroundToken(e.target.value)}
                  placeholder="Neues Hintergrund-Token"
                  className="flex-1 px-2 py-1 border rounded text-sm"
                />
                <button
                  onClick={addBackgroundToken}
                  disabled={!newBackgroundToken}
                  className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  + Hintergrund
                </button>
              </div>
            </div>

            {allBackgroundTokens.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Hintergrund ({allBackgroundTokens.length} Tokens) — mehrere möglich</h4>
                <div className="space-y-2">
                  {allBackgroundTokens.map(token => (
                    <div key={token} className="border rounded p-2 bg-gray-50">
                      <div className="flex items-center gap-2 mb-1">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">{token}</code>
                        {manualBackgroundTokens.includes(token) && (
                          <span className="text-xs text-blue-600">(manuell)</span>
                        )}
                        {(localBackgroundMapping[token]?.length || 0) > 1 && (
                          <span className="text-xs text-green-600">({localBackgroundMapping[token]?.length} ausgewählt)</span>
                        )}
                        {manualBackgroundTokens.includes(token) && (
                          <button
                            onClick={() => removeBackgroundToken(token)}
                            className="text-xs text-red-500 hover:text-red-700 ml-auto"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {availableBackgrounds.map(b => (
                          <label key={b} className="flex items-center gap-1 text-xs cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(localBackgroundMapping[token] || []).includes(b)}
                              onChange={() => toggleBackgroundSelection(token, b)}
                              className="w-3 h-3"
                            />
                            <span className={(localBackgroundMapping[token] || []).includes(b) ? 'font-medium text-green-700' : 'text-gray-600'}>
                              {b}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 justify-end pt-4 border-t">
              <button
                onClick={() => onShowTokenMappingModal(false)}
                className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  onSubjectTokenMappingChange(localSubjectMapping)
                  onBackgroundTokenMappingChange(localBackgroundMapping)
                  onShowTokenMappingModal(false)
                }}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Anwenden
              </button>
            </div>
          </div>
        </div>
      )}

      {canGenerateEmail && !showEmailGenerationModal && emailGenerationConfig && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm font-medium text-blue-800 mb-1">E-Mail wird automatisch generiert:</p>
          <p className="text-xs text-blue-700">
            Pattern: <code className="bg-blue-100 px-1 rounded">{emailGenerationConfig.pattern}</code>@<code className="bg-blue-100 px-1 rounded">{emailGenerationConfig.domain}</code>
            {emailGenerationConfig.lowercase && ' (kleinbuchstaben)'}
          </p>
          <button
            onClick={() => onShowEmailGenerationModal(true)}
            className="text-xs text-blue-600 hover:underline mt-1"
          >
            Konfiguration ändern
          </button>
        </div>
      )}

      {showEmailGenerationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">E-Mail generieren</h3>
            <p className="text-sm text-gray-600 mb-4">
              Keine E-Mail-Spalte gefunden. E-Mails können automatisch aus Vorname und Nachname generiert werden.
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">E-Mail-Format:</label>
                <select
                  value={showCustomPatternInput ? 'custom' : localEmailPattern}
                  onChange={e => {
                    if (e.target.value === 'custom') {
                      setShowCustomPatternInput(true)
                    } else {
                      setShowCustomPatternInput(false)
                      setLocalEmailPattern(e.target.value)
                    }
                  }}
                  className="w-full px-3 py-2 border rounded text-sm bg-white"
                >
                  {EMAIL_PATTERN_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {showCustomPatternInput && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Eigene Logik:</label>
                  <input
                    type="text"
                    value={localEmailPattern}
                    onChange={e => setLocalEmailPattern(e.target.value)}
                    placeholder="z.B. {first_name[0:3]}.{last_name}"
                    className="w-full px-3 py-2 border rounded text-sm"
                  />
                  <pre className="text-xs text-gray-500 mt-2 bg-gray-50 p-2 rounded whitespace-pre-wrap">{PLACEHOLDER_HELP}</pre>
                </div>
              )}

              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                <span className="font-medium">Domain:</span>{' '}
                {localEmailDomain ? (
                  <code className="bg-gray-100 px-1 rounded">{localEmailDomain}</code>
                ) : (
                  <span className="text-gray-400">Wird automatisch von der Schule übernommen</span>
                )}
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={localLowercase}
                  onChange={e => setLocalLowercase(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm">Kleinbuchstaben</span>
              </label>

              {detectedSpecialChars.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Spezialzeichen in Namen gefunden:
                  </h4>
                  <div className="space-y-1">
                    {detectedSpecialChars.map(char => (
                      <div key={char} className="flex items-center gap-2">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded flex-shrink-0 w-8 text-center">{char}</code>
                        <span className="text-xs text-gray-500">→</span>
                        <input
                          type="text"
                          value={localCharMappings[char] || SPECIAL_CHAR_DEFAULTS[char] || ''}
                          onChange={e => {
                            const newMappings = { ...localCharMappings, [char]: e.target.value }
                            setLocalCharMappings(newMappings)
                          }}
                          placeholder={SPECIAL_CHAR_DEFAULTS[char] || ''}
                          className="flex-1 px-2 py-1 border rounded text-sm"
                        />
                        <span className="text-xs text-gray-400">
                          (Standard: {SPECIAL_CHAR_DEFAULTS[char] || '—'})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-end pt-4 border-t mt-4">
              <button
                onClick={() => {
                  setEmailModalDismissed(true)
                  onShowEmailGenerationModal(false)
                }}
                className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  onEmailGenerationConfigChange({
                    pattern: localEmailPattern,
                    domain: localEmailDomain,
                    charMappings: localCharMappings,
                    lowercase: localLowercase,
                  })
                  setEmailModalDismissed(false)
                  onShowEmailGenerationModal(false)
                }}
                disabled={!localEmailDomain}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                Anwenden
              </button>
            </div>
          </div>
        </div>
      )}

      {showRowBackgroundModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">Hintergrund manuell wählen</h3>
            <p className="text-sm text-gray-600 mb-2">Zeile {showRowBackgroundModal.rowIndex + 1}</p>
            <p className="text-xs text-red-600 mb-3">{showRowBackgroundModal.errors.join(', ')}</p>
            <select
              value={selectedRowBackground}
              onChange={e => setSelectedRowBackground(e.target.value)}
              className="w-full px-2 py-1.5 border rounded text-sm mb-4"
            >
              <option value="">-- Hintergrund wählen --</option>
              {availableBackgrounds.map(b => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => onShowRowBackgroundModal?.(-1, [])}
                className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  if (selectedRowBackground) {
                    onRowBackgroundSelect(showRowBackgroundModal.rowIndex, selectedRowBackground)
                    setSelectedRowBackground('')
                  }
                }}
                disabled={!selectedRowBackground}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                Anwenden
              </button>
            </div>
          </div>
        </div>
      )}

      {sheets.length > 1 && (
        <div className="bg-white rounded-lg border p-4">
          <label className="text-sm font-medium text-gray-700 mr-2">Arbeitsblatt:</label>
          <select
            value={selectedSheet}
            onChange={e => onSelectSheet(e.target.value)}
            className="px-3 py-1.5 border rounded text-sm"
          >
            {sheets.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      )}

      <div className="bg-white rounded-lg border p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Spalten zuordnen</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {columnMapping.map((m) => {
            const previewValues = rawRows.slice(0, 3).map(row => {
              const val = row[m.fileColumn] || ''
              return val.length > 30 ? val.slice(0, 30) + '...' : val
            })
            const confidenceColor = m.confidence >= 1.0 ? 'text-green-600' : m.confidence >= 0.7 ? 'text-yellow-600' : 'text-gray-400'
            const cardBg = m.systemField && m.isAutoDetected
              ? (m.confidence >= 1.0 ? 'bg-green-50 border-green-200' : m.confidence >= 0.7 ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50')
              : 'bg-gray-50'

            return (
              <div key={m.fileColumn} className={`border rounded-lg p-3 ${cardBg}`}>
                <div className="text-sm font-medium text-gray-700 mb-1">{m.fileColumn}</div>
                <div className="text-xs text-gray-400 mb-2">
                  {previewValues.map((v, i) => (
                    <div key={i} className="truncate">{v || '—'}</div>
                  ))}
                </div>
                <select
                  value={m.systemField || ''}
                  onChange={e => onMappingChange(m.fileColumn, e.target.value || null)}
                  className="w-full px-2 py-1.5 border rounded text-sm mb-1 bg-white"
                >
                  <option value="">Ignorieren</option>
                  {availableFields.map(f => (
                    <option key={f} value={f}>
                      {FIELD_LABELS[f] || f}
                      {requiredFields.includes(f) ? ' *' : ''}
                    </option>
                  ))}
                </select>
                {m.systemField && (
                  <div className={`text-xs ${confidenceColor}`}>
                    {m.confidence >= 1.0 ? '✓ 100%' : m.confidence >= 0.7 ? `✓ ${Math.round(m.confidence * 100)}%` : 'Vermutung'}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {hasCombinedName && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm text-gray-600 mb-2">Name-Format:</p>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="splitMode"
                  checked={splitMode === 'last_first'}
                  onChange={() => onSplitModeChange('last_first')}
                  className="w-4 h-4"
                />
                <span className="text-sm">Name, Vorname</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="splitMode"
                  checked={splitMode === 'first_last'}
                  onChange={() => onSplitModeChange('first_last')}
                  className="w-4 h-4"
                />
                <span className="text-sm">Vorname, Name</span>
              </label>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">
            Vorschau
            {previewStats && (
              <span className="ml-2 text-xs text-gray-500">
                {totalRowsActual > previewRows.length
                  ? `(${previewRows.length} von ${totalRowsActual} Zeilen · ${previewStats.toCreate} neu · ${previewStats.toUpdate} Update · ${previewStats.errorCount} Fehler)`
                  : `(${totalRowsActual} Zeilen · ${previewStats.toCreate} neu · ${previewStats.toUpdate} Update · ${previewStats.errorCount} Fehler)`
                }
              </span>
            )}
          </h3>
          {loading && (
            <div className="flex items-center gap-2 text-sm text-blue-700">
              <div className="animate-spin w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full" />
              Vorschau wird aktualisiert…
            </div>
          )}
        </div>

        {loading && (
          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            Spaltenzuordnung wird neu berechnet. Die Tabelle unten kann kurz veraltet sein.
          </div>
        )}

        {executing && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 max-w-sm mx-4 text-center shadow-lg">
              <div className="animate-spin w-10 h-10 border-4 border-gray-200 border-t-green-600 rounded-full mx-auto mb-4" />
              <p className="font-semibold text-gray-900">Import läuft…</p>
              <p className="text-sm text-gray-600 mt-2">Bitte warte und schliesse das Fenster nicht. Bei vielen Zeilen kann das einige Minuten dauern.</p>
            </div>
          </div>
        )}

        {previewRows.length > 0 ? (
          <div className={`overflow-x-auto max-h-[500px] overflow-y-auto ${loading ? 'opacity-60' : ''}`}>
            <table className="min-w-full border text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-2 border text-left">#</th>
                  <th className="px-2 py-2 border text-left">Vorname</th>
                  <th className="px-2 py-2 border text-left">Nachname</th>
                  <th className="px-2 py-2 border text-left">E-Mail</th>
                  <th className="px-2 py-2 border text-left">Kürzel</th>
                  <th className="px-2 py-2 border text-left">Fächer</th>
                  <th className="px-2 py-2 border text-left">Hintergrund</th>
                  <th className="px-2 py-2 border text-left">Rolle</th>
                  <th className="px-2 py-2 border text-left">Status</th>
                  <th className="px-2 py-2 border text-left">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row) => {
                  const isSkipped = skipRowIndices.has(row.row_index)
                  const hasErrors = row.validation_errors.length > 0
                  const hasSubjectErrors = row.subject_mapping_errors && row.subject_mapping_errors.length > 0
                  const roleOverride = roleOverrides.get(row.row_index)
                  const effectiveRole = roleOverride || row.effective_role || targetRole
                  const isTeacherRole = effectiveRole === 'teacher'
                  const hasBackgroundErrors = isTeacherRole && row.background_mapping_errors && row.background_mapping_errors.length > 0
                  const hasMappingErrors = hasSubjectErrors || hasBackgroundErrors
                  const editKey = `${row.row_index}${CELL_KEY_SEPARATOR}email`
                  const editedEmail = editedCells.get(editKey)

                  return (
                    <tr
                      key={row.row_index}
                      className={`${isSkipped ? 'opacity-50' : ''} ${hasErrors || hasMappingErrors ? 'bg-red-50' : row.exists ? 'bg-yellow-50' : ''}`}
                    >
                      <td className="px-2 py-2 border">{row.row_index + 1}</td>
                      <td className="px-2 py-2 border">{row.first_name}</td>
                      <td className="px-2 py-2 border">{row.last_name}</td>
                      <td className="px-2 py-2 border">
                        <input
                          type="text"
                          value={editedEmail ?? row.email ?? ''}
                          onChange={e => onEditCell(row.row_index, 'email', e.target.value)}
                          className={`w-full px-1 py-0.5 border rounded text-xs ${editedEmail ? 'border-blue-400' : ''}`}
                        />
                      </td>
                      <td className="px-2 py-2 border">
                        {(() => {
                          const editKey = `${row.row_index}${CELL_KEY_SEPARATOR}teacher_code`
                          const editedTeacherCode = editedCells.get(editKey)
                          const displayCode = editedTeacherCode ?? row.teacher_code ?? ''
                          const isEdited = editedTeacherCode !== undefined
                          const isNumbered = row.is_numbered_teacher_code

                          return (
                            <input
                              type="text"
                              value={displayCode}
                              onChange={e => onEditCell(row.row_index, 'teacher_code', e.target.value)}
                              className={`w-full px-1 py-0.5 border rounded text-xs ${
                                isEdited ? 'border-blue-400' :
                                isNumbered ? 'border-red-300 text-red-600 font-semibold' :
                                row.is_generated_teacher_code ? 'border-blue-200 text-blue-600 italic' :
                                ''
                              }`}
                              title={isNumbered ? 'Automatisch durchnummeriert (Kollision)' : row.is_generated_teacher_code ? 'Automatisch generiert' : ''}
                            />
                          )
                        })()}
                      </td>
                      <td className="px-2 py-2 border">
                        {row.subjects && row.subjects.length > 0 ? (
                          <span className={`text-xs ${hasSubjectErrors ? 'text-red-600' : 'text-green-700'}`}>
                            {row.subjects.join(', ')}
                            {hasSubjectErrors && <span className="ml-1 text-red-500">⚠</span>}
                          </span>
                        ) : hasSubjectErrors ? (
                          <span className="text-xs text-red-600">{row.subject_mapping_errors?.join(', ')}</span>
                        ) : ''}
                      </td>
                      <td className="px-2 py-2 border">
                        {isTeacherRole ? (
                          <>
                            {row.backgrounds && row.backgrounds.length > 0 && (
                              <span className={`text-xs ${hasBackgroundErrors ? 'text-red-600' : 'text-green-700'}`}>
                                {row.backgrounds.join(', ')}
                              </span>
                            )}
                            {hasBackgroundErrors && (
                              <span
                                className="text-xs text-red-600 cursor-pointer hover:underline ml-1"
                                onClick={() => onShowRowBackgroundModal?.(row.row_index, row.background_mapping_errors || [])}
                                title="Klicken zum Mappen"
                              >
                                ⚠ {row.background_mapping_errors?.join(', ')}
                              </span>
                            )}
                          </>
                        ) : ''}
                      </td>
                      <td className="px-2 py-2 border">
                        <select
                          value={effectiveRole}
                          onChange={e => {
                            if (e.target.value === targetRole) {
                              onRoleOverrideClear(row.row_index)
                            } else {
                              onRoleOverrideChange(row.row_index, e.target.value)
                            }
                          }}
                          className={`px-1 py-0.5 border rounded text-xs bg-white ${roleOverride ? 'border-blue-400 bg-blue-50' : ''}`}
                        >
                          {roles.map(r => (
                            <option key={r.role} value={r.role}>{r.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-2 border">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          isSkipped ? 'bg-gray-100 text-gray-600' :
                          hasErrors || hasMappingErrors ? 'bg-red-100 text-red-700' :
                          row.exists ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {isSkipped ? 'Übersprungen' :
                           hasErrors || hasMappingErrors ? 'Fehler' :
                           row.exists ? 'Update' : 'Neu'}
                        </span>
                      </td>
                      <td className="px-2 py-2 border">
                        {isSkipped ? (
                          <button
                            onClick={() => onUnskipRow(row.row_index)}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Wiederherstellen
                          </button>
                        ) : (
                          <button
                            onClick={() => onSkipRow(row.row_index)}
                            className="text-xs text-gray-500 hover:underline"
                          >
                            Überspringen
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-400 text-sm">
            {loading ? 'Vorschau wird erstellt...' : 'Keine Daten verfügbar'}
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={updateExisting}
              onChange={e => onUpdateExistingChange(e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-sm">Bestehende aktualisieren</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={skipDuplicates}
              onChange={e => onSkipDuplicatesChange(e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-sm">Duplikate überspringen</span>
          </label>
        </div>
        <div className="flex items-center gap-3">
          {missingRequired.length > 0 && (
            <span className="text-xs text-red-600">
              Pflichtfelder fehlen: {missingRequired.map(f => FIELD_LABELS[f] || f).join(', ')}
            </span>
          )}
          <button
            onClick={onExecute}
            disabled={!canImport || executing}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
          >
            {executing
              ? 'Import läuft...'
              : previewStats
                ? `Import starten (${totalRowsActual} Zeilen)`
                : 'Import starten'
            }
          </button>
        </div>
      </div>
    </div>
  )
}

function ResultStep({
  result,
  onNewImport,
  onCancel,
}: {
  result: ImportExecuteResult
  onNewImport: () => void
  onCancel?: () => void
}) {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-lg font-semibold mb-4">Import-Ergebnis</h2>
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="bg-green-50 border rounded p-3 text-center">
            <div className="text-xl font-bold text-green-700">{result.created}</div>
            <div className="text-xs text-gray-600">Erstellt</div>
          </div>
          <div className="bg-blue-50 border rounded p-3 text-center">
            <div className="text-xl font-bold text-blue-700">{result.updated}</div>
            <div className="text-xs text-gray-600">Aktualisiert</div>
          </div>
          <div className="bg-gray-50 border rounded p-3 text-center">
            <div className="text-xl font-bold text-gray-700">{result.skipped}</div>
            <div className="text-xs text-gray-600">Übersprungen</div>
          </div>
          <div className="bg-red-50 border rounded p-3 text-center">
            <div className="text-xl font-bold text-red-700">{result.errored}</div>
            <div className="text-xs text-gray-600">Fehler</div>
          </div>
        </div>
        {result.warnings.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mb-4">
            {result.warnings.map((w, i) => (
              <p key={i} className="text-xs text-yellow-700">{w}</p>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <button
          onClick={onNewImport}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
        >
          Neuen Import starten
        </button>
        {onCancel && (
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
          >
            Zurück zur Übersicht
          </button>
        )}
      </div>
    </div>
  )
}

export default PersonalImportEditor

function TeacherCodeGenerationModal({
  suggestions,
  stats,
  hasFileCodeColumn,
  onSkip,
  onApply,
}: {
  suggestions: TeacherCodeSuggestions | null
  stats: TeacherCodeStats | null
  hasFileCodeColumn: boolean
  onSkip: () => void
  onApply: (config: TeacherCodeGenerationConfig | null, overwrite: boolean) => void
}) {
  const [localMethod, setLocalMethod] = useState<'file' | 'rule' | 'pattern'>('file')
  const [localRuleType, setLocalRuleType] = useState<string>(
    suggestions?.availableRules?.[0]?.type || 'first3_last'
  )
  const [localPatternSyntax, setLocalPatternSyntax] = useState<string>(
    suggestions?.autoInferredPattern?.pattern_syntax || ''
  )
  const [localOverwrite, setLocalOverwrite] = useState(false)

  const hasAutoPattern = !!suggestions?.autoInferredPattern?.pattern_syntax
  const availableRules = suggestions?.availableRules || []

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onSkip}>
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-2">Lehrer-Kürzel</h3>
        <p className="text-sm text-gray-600 mb-4">
          {stats?.rows_without_code || 0} von {stats?.total_teacher_rows || 0} Lehrern haben kein Kürzel.
        </p>

        <div className="space-y-3 mb-4">
          <label className="flex items-start gap-3 p-3 border rounded cursor-pointer hover:bg-blue-50">
            <input
              type="radio"
              name="codeMethod"
              value="file"
              checked={localMethod === 'file'}
              onChange={() => setLocalMethod('file')}
              className="mt-1"
            />
            <div className="w-full">
              <div className="text-sm font-medium">Kürzel aus der Liste importieren</div>
              <p className="text-xs text-gray-500 mt-1">
                {hasFileCodeColumn
                  ? 'Bestehende Kürzel-Spalte der Import-Datei verwenden.'
                  : 'Keine Kürzel-Spalte zugeordnet. Nach dem Schließen im Mapping «Kürzel» wählen.'}
              </p>
            </div>
          </label>

          <label className="flex items-start gap-3 p-3 border rounded cursor-pointer hover:bg-blue-50">
            <input
              type="radio"
              name="codeMethod"
              value="pattern"
              checked={localMethod === 'pattern'}
              onChange={() => setLocalMethod('pattern')}
              className="mt-1"
            />
            <div className="w-full">
              <div className="text-sm font-medium">
                Muster-basiert (Klammern)
                {suggestions?.recommendation === 'pattern' && (
                  <span className="ml-2 text-xs text-green-600">✓ Empfohlen</span>
                )}
              </div>
              {hasAutoPattern ? (
                <div className="text-xs text-gray-500 mt-1">
                  Muster: <code>{suggestions!.autoInferredPattern!.pattern_syntax}</code>
                  {' '}· Confidence: {Math.round(suggestions!.autoInferredPattern!.confidence * 100)}%
                </div>
              ) : (
                <div className="mt-1">
                  <input
                    type="text"
                    value={localPatternSyntax}
                    onChange={e => setLocalPatternSyntax(e.target.value)}
                    placeholder="z.B. (W O) )L F( (M)"
                    className="w-full px-2 py-1 border rounded text-sm"
                    onClick={e => e.stopPropagation()}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    (ABC) = nehmen, )ABC( = springen. Beispiel: (W O) )L F( (M) aus "Marc Wolf" → WOM
                  </p>
                </div>
              )}
            </div>
          </label>

          <label className="flex items-start gap-3 p-3 border rounded cursor-pointer hover:bg-blue-50">
            <input
              type="radio"
              name="codeMethod"
              value="rule"
              checked={localMethod === 'rule'}
              onChange={() => setLocalMethod('rule')}
              className="mt-1"
            />
            <div className="w-full">
              <div className="text-sm font-medium">Regel-basiert</div>
              <select
                value={localRuleType}
                onChange={e => setLocalRuleType(e.target.value)}
                className="mt-1 w-full px-2 py-1 border rounded text-sm bg-white"
              >
                {availableRules.map(r => (
                  <option key={r.type} value={r.type}>{r.description}</option>
                ))}
              </select>
            </div>
          </label>
        </div>

        {localMethod !== 'file' && (
          <label className="flex items-center gap-2 mb-4">
            <input
              type="checkbox"
              checked={localOverwrite}
              onChange={e => setLocalOverwrite(e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-sm">Bestehende Kürzel überschreiben</span>
          </label>
        )}

        <div className="flex gap-2 justify-end pt-4 border-t">
          <button
            onClick={onSkip}
            className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
          >
            Abbrechen
          </button>
          <button
            onClick={() => {
              if (localMethod === 'file') {
                onApply(null, false)
                return
              }
              onApply({
                method: localMethod,
                ruleType: localMethod === 'rule' ? localRuleType : null,
                patternSyntax: localMethod === 'pattern' ? (localPatternSyntax || null) : null,
              }, localOverwrite)
            }}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Anwenden
          </button>
        </div>
      </div>
    </div>
  )
}